import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useVideoSync Hook
 * 
 * Synchronizes video playback with transcript blocks
 * Handles auto-scrolling and highlighting of active block
 * 
 * @param {Array} blocks - Transcript blocks
 * @param {React.RefObject} videoRef - Reference to video element
 * @param {Object} options - Configuration options
 * @returns {Object} Sync state and control functions
 */
const useVideoSync = (blocks = [], videoRef, options = {}) => {
  // Safety check: ensure blocks is always an array
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const {
    autoScroll = true,
    scrollBehavior = 'smooth',
    scrollBlock = 'center',
    updateInterval = 100, // ms
  } = options;

  const [currentBlockId, setCurrentBlockId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  
  const previousBlockIdRef = useRef(null);
  const previousWordIndexRef = useRef(-1);
  const lastTimeEmitRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const eventListenersSetRef = useRef(false);
  const isManualSeekRef = useRef(false);

  /**
   * Find active block based on current time
   */
  const findActiveBlock = useCallback(
    (time) => {
      return safeBlocks.find((block) => time >= block.start && time < block.end);
    },
    [safeBlocks]
  );

  /**
   * Find active word within a block based on current time
   */
  const findActiveWord = useCallback(
    (time, block) => {
      if (!block || !block.words || block.words.length === 0) {
        return { wordIndex: -1, word: null };
      }
      
      const wordIndex = block.words.findIndex(
        (word) => time >= word.start && time < word.end
      );
      
      return {
        wordIndex,
        word: wordIndex >= 0 ? block.words[wordIndex] : null,
      };
    },
    []
  );

  /**
   * Scroll to specific block
   * DISABLED - Scroll functionality removed for now
   */
  const scrollToBlock = useCallback(
    (blockId, behavior = scrollBehavior) => {
      // Scroll completely disabled
      return;
    },
    [autoScroll, scrollBehavior, scrollBlock]
  );

  /**
   * Seek video to specific time
   */
  const seekTo = useCallback(
    (time) => {
      return new Promise((resolve, reject) => {
        if (!videoRef.current) {
          reject(new Error('Video element not available'));
          return;
        }
        
        if (typeof time !== 'number' || !isFinite(time) || time < 0) {
          reject(new Error('Invalid time parameter'));
          return;
        }
        
        const video = videoRef.current;
        
        // If already at the target time (within tolerance), resolve immediately
        if (Math.abs(video.currentTime - time) < 0.1) {
          setCurrentTime(time);
          resolve(time);
          return;
        }
        
        // Mark as manual seek to prevent auto-scroll
        isManualSeekRef.current = true;
        
        // Set up one-time 'seeked' event listener
        const handleSeeked = () => {
          setCurrentTime(video.currentTime);
          video.removeEventListener('seeked', handleSeeked);
          clearTimeout(timeoutId);
          
          // Reset manual seek flag after a short delay
          setTimeout(() => {
            isManualSeekRef.current = false;
          }, 500);
          
          resolve(video.currentTime);
        };
        
        // Set up timeout fallback
        const timeoutId = setTimeout(() => {
          video.removeEventListener('seeked', handleSeeked);
          setCurrentTime(video.currentTime);
          isManualSeekRef.current = false;
          resolve(video.currentTime);
        }, 5000);
        
        video.addEventListener('seeked', handleSeeked);
        
        // Perform the seek
        try {
          video.currentTime = time;
        } catch (error) {
          video.removeEventListener('seeked', handleSeeked);
          clearTimeout(timeoutId);
          isManualSeekRef.current = false;
          reject(error);
        }
      });
    },
    [videoRef]
  );

  /**
   * Seek to block start time
   */
  const seekToBlock = useCallback(
    async (blockId) => {
      // Flexible ID matching (handle string vs number)
      const block = safeBlocks.find((b) => 
        b.id === blockId || 
        b.id === parseInt(blockId) || 
        String(b.id) === String(blockId)
      );
      
      if (block) {
        return await seekTo(block.start);
      }
      
      return Promise.reject(new Error('Block not found'));
    },
    [safeBlocks, seekTo]
  );

  /**
   * Seek to specific word within a block
   */
  const seekToWord = useCallback(
    async (blockId, wordIndex) => {
      const block = safeBlocks.find((b) => 
        b.id === blockId || 
        b.id === parseInt(blockId) || 
        String(b.id) === String(blockId)
      );
      
      if (!block) {
        return Promise.reject(new Error('Block not found'));
      }
      
      if (!block.words || wordIndex < 0 || wordIndex >= block.words.length) {
        return Promise.reject(new Error('Invalid word index'));
      }
      
      const word = block.words[wordIndex];
      return await seekTo(word.start);
    },
    [safeBlocks, seekTo]
  );

  /**
   * Play/pause video
   */
  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [videoRef]);

  /**
   * Play video
   */
  const play = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Error handling is done silently
      });
    }
  }, [videoRef]);

  /**
   * Pause video
   */
  const pause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [videoRef]);

  /**
   * Skip forward/backward
   */
  const skip = useCallback(
    async (seconds) => {
      if (videoRef.current && videoRef.current.duration && isFinite(videoRef.current.duration)) {
        const newTime = Math.max(
          0,
          Math.min(videoRef.current.duration, videoRef.current.currentTime + seconds)
        );
        return await seekTo(newTime);
      }
      return Promise.reject(new Error('Video not ready'));
    },
    [videoRef, seekTo]
  );

  /**
   * Set playback speed
   */
  const setPlaybackRate = useCallback(
    (rate) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = rate;
      }
    },
    [videoRef]
  );

  /**
   * Detect user scrolling
   */
  useEffect(() => {
    const handleScroll = () => {
      isUserScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 1000);
    };

    const transcriptContainer = document.getElementById('transcript-blocks-container');
    if (transcriptContainer) {
      transcriptContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (transcriptContainer) {
        transcriptContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);


  /**
   * Video event listeners - Setup with polling until video is ready
   */
  useEffect(() => {
    let handleTimeUpdate;
    let handlePlay;
    let handlePause;
    let handleLoadedMetadata;
    let handleSeeking;
    let interval;
    let timeout;
    
    const setupEventListeners = () => {
      const video = videoRef.current;
      
      if (!video) {
        return false;
      }
      
      if (eventListenersSetRef.current) {
        return true;
      }

      handleTimeUpdate = () => {
        const time = video.currentTime;
        // Throttle state updates to reduce global re-render pressure.
        // Still computes active block/word on every tick for correctness.
        const now = performance.now();
        const shouldEmit = now - lastTimeEmitRef.current >= updateInterval;
        if (shouldEmit) {
          lastTimeEmitRef.current = now;
          setCurrentTime(time);
        }

        // Find and set active block
        const activeBlock = findActiveBlock(time);
        if (activeBlock && activeBlock.id !== previousBlockIdRef.current) {
          setCurrentBlockId(activeBlock.id);
          previousBlockIdRef.current = activeBlock.id;
          // Emit a time update immediately on active-block change (better UX).
          lastTimeEmitRef.current = now;
          setCurrentTime(time);
        } else if (!activeBlock && previousBlockIdRef.current) {
          setCurrentBlockId(null);
          previousBlockIdRef.current = null;
          setCurrentWordIndex(-1);
          previousWordIndexRef.current = -1;
          lastTimeEmitRef.current = now;
          setCurrentTime(time);
        }

        // Find and set active word (if block has word-level data)
        if (activeBlock) {
          const { wordIndex } = findActiveWord(time, activeBlock);
          if (wordIndex !== previousWordIndexRef.current) {
            setCurrentWordIndex(wordIndex);
            previousWordIndexRef.current = wordIndex;
          }
        }
      };

      handlePlay = () => {
        setIsPlaying(true);
      };
      
      handlePause = () => {
        setIsPlaying(false);
      };
      
      handleLoadedMetadata = () => {
        setDuration(video.duration);
      };
      
      handleSeeking = () => handleTimeUpdate();

      // Set up event listeners
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('seeking', handleSeeking);

      // Initial setup
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }

      eventListenersSetRef.current = true;
      
      return true;
    };
    
    // Try to set up immediately
    const success = setupEventListeners();
    
    if (!success) {
      // If not successful, poll every 200ms
      interval = setInterval(() => {
        const success = setupEventListeners();
        if (success) {
          clearInterval(interval);
        }
      }, 200);
      
      // Stop polling after 10 seconds
      timeout = setTimeout(() => {
        if (interval) clearInterval(interval);
      }, 10000);
    }
    
    // Cleanup function
    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      
      const video = videoRef.current;
      if (video && eventListenersSetRef.current && handleTimeUpdate) {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('seeking', handleSeeking);
        eventListenersSetRef.current = false;
      }
    };
  }, [videoRef, safeBlocks, findActiveBlock, findActiveWord]);

  return {
    currentBlockId,
    currentTime,
    duration,
    isPlaying,
    currentWordIndex,
    seekTo,
    seekToBlock,
    seekToWord,
    togglePlay,
    play,
    pause,
    skip,
    setPlaybackRate,
    scrollToBlock,
  };
};

export default useVideoSync;


